<script lang="ts">
	import type { Calibration } from "luxedo-data"
	import MaskEditorCanvas from "./MaskEditor_Canvas.svelte"
	import MaskEditorSidebar from "./sidebar/MaskEditorSidebar.svelte"
	import { onDestroy, onMount } from "svelte"
	import { MaskEditorController } from "./MaskEditorController"
	import { MaskSidebarController } from "./sidebar/MaskEditorSidebarController"
	import { LoadingSpinner } from "misc-ui"
	import { getEditor } from "luxedo-canvas"
	import { AddMask, DrawPath, PolygonEditMode, TransformMode } from "luxedo-canvas/lib"

	const MENU_CAN_SAVE: Array<MaskSidebarController.MenuOption> = ["list"]

	interface Props {
		calibration: Calibration

		onSuccess: () => void
		onFailure: () => void
	}

	let { calibration, onSuccess, onFailure }: Props = $props()

	let activeMenu: MaskSidebarController.MenuOption = $state()

	let isSaving: boolean = $state(false)

	let isTransforming = $state(false)
	let isCreating = $state(false)
	let isSelected = $state(false)

	let showCanvas = $derived(isCreating || isSelected)

	MaskSidebarController.subscribe((ctx) => {
		activeMenu = ctx.menu
	})

	async function save() {
		isSaving = true
		try {
			await MaskEditorController.save()
			onSuccess()
		} catch (e) {
			console.error("An error occurred while saving mask data...", e)
			onFailure()
		}
		isSaving = false
	}

	onMount(() => {
		MaskEditorController.initialize(calibration)

		const modeListener = getEditor().mode.on("*", () => {
			const mode = getEditor().mode.get()

			isCreating =
				mode instanceof AddMask ||
				mode instanceof DrawPath ||
				mode instanceof TransformMode ||
				mode instanceof PolygonEditMode
			isTransforming = mode instanceof TransformMode || mode instanceof PolygonEditMode
		})

		const selectionListener = getEditor().selection.on("*", () => {
			isSelected = getEditor().selection.get().length > 0
		})

		return () => {
			modeListener.remove()
			selectionListener.remove()
		}
	})

	onDestroy(() => {
		MaskEditorController.reset()
	})
</script>

<div id="mask-editor">
	<MaskEditorSidebar {showCanvas} />
	<MaskEditorCanvas {showCanvas} hasSelection={isSelected} {isTransforming} />
	{#if MENU_CAN_SAVE.includes(activeMenu)}
		<div id="save-button-container">
			{#if isSaving}
				<LoadingSpinner height="2rem" color="var(--color-main)" />
			{:else}
				<button id="save-masks-button" onclick={save}>Save</button>
			{/if}
		</div>
	{/if}
</div>

<style>
	#mask-editor {
		--sidebar-width: 20rem;
		--mask-preview-height: 8rem;

		height: 80vh;
		width: 80vw;
		display: flex;
		flex-direction: row;
	}

	:global(.isMobile) #mask-editor {
		height: 100%;
		width: 100%;

		--sidebar-width: 100%;

		flex-direction: column;
	}

	:global(.isMobile.overlay):has(#mask-editor) {
		padding: 0;
		width: 100dvw;
		max-width: 100dvw;
		height: 100dvh;
		max-height: 100dvh;
		border-radius: unset;
	}

	#save-button-container {
		position: absolute;
		bottom: 1rem;
		right: 1rem;
	}

	:global(.isMobile) #save-button-container {
		top: 0.5rem;
		right: 3rem;
	}

	:global(.isMobile) button {
		font-size: var(--h3);
		padding: 0 0.5rem;
	}

	:global(.isMobile) #mask-editor :global(.button-container) {
		gap: 0.5rem;
	}

	:global(.isMobile)
		#mask-editor:has(:global(#canvas-wrapper:not(.isHidden)))
		:global(#sidebar:has(.mask-list)) {
		overflow: hidden;
	}
</style>
